import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { MediaPlansDeleteModal } from '../../features/media-buys/media-plans-action-modals';
import { Tooltip } from 'react-tooltip';
import { generateDeeplink } from '../../features/media-buys/platform-buy-utils';
import { clsx } from 'clsx';
import { MediaPlanTableData } from '@/features/media-plans/media-plans-table';
import { trpc } from '@/utils/trpc-client';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';

type Props = {
  data: MediaPlanTableData;
  lineItem: ExecutionLineItemTrpcResponse;
};

export function MediaPlansActions(props: Props) {
  const { data } = props;
  return (
    <div className="">
      <div className="shadow-l-lg fixed right-0 h-full w-16 border-l border-gray-200 bg-white px-2">
        <div className="flex h-full w-full items-center justify-between">
          {data.media_buy && (
            <>
              <ViewOnPlatformButton data={data} />
              <DeleteMediaPlanButton {...props} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewOnPlatformButton({ data }: { data: MediaPlanTableData }) {
  const link = data.media_buy ? generateDeeplink(data.media_buy) : undefined;
  return (
    <a href={link} target="_blank" rel="noreferrer">
      <ActionButton
        id="view-on-platform-button"
        disabled={link === undefined}
        onClick={() => {}}
        icon={faArrowUpRightFromSquare}
        iconColor="text-blue-600"
        tooltipContent="View on platform"
      />
    </a>
  );
}

function DeleteMediaPlanButton({ data, lineItem }: Props) {
  const [modalOpen, setModalOpen] = React.useState(false);
  const { mutateAsync, isPending } = trpc.deleteMediaPlan.useMutation();
  const utils = trpc.useUtils();

  async function deleteMediaPlan() {
    if (isPending) return;
    const { strategy_id, id: line_item_id } = lineItem;
    await mutateAsync({ strategy_id, line_item_id, media_plan_id: data.id });
    setModalOpen(false);
    await utils.invalidate();
  }

  return (
    <>
      <ActionButton
        id="delete-media-plan-button"
        disabled={isPending}
        onClick={() => setModalOpen(true)}
        icon={faTrashCan}
        iconColor="text-red-600"
        tooltipContent="Delete Media Plan"
      />
      <MediaPlansDeleteModal
        open={modalOpen}
        setOpen={setModalOpen}
        onConfirm={deleteMediaPlan}
        names={data.name ? [data.name] : ['No name entered']}
        spendAmount={data.metrics?.actual_media_spend}
      />
    </>
  );
}

type ActionButtonProps = {
  id: string;
  disabled: boolean;
  onClick: () => void;
  icon: IconDefinition;
  iconColor: string;
  tooltipContent: React.ReactNode;
};

function ActionButton({
  id,
  disabled,
  onClick,
  icon,
  iconColor,
  tooltipContent
}: ActionButtonProps) {
  return (
    <div>
      <button
        id={id}
        disabled={disabled}
        onClick={onClick}
        className={clsx('flex w-4 items-center disabled:text-gray-400 cursor-pointer', iconColor)}>
        <FontAwesomeIcon icon={icon} />
      </button>
      <Tooltip
        anchorSelect={`#${id}`}
        place="bottom"
        positionStrategy="fixed"
        style={{ zIndex: 9999, paddingTop: 0, paddingBottom: 0 }}
        delayShow={500}>
        {tooltipContent}
      </Tooltip>
    </div>
  );
}
